import {useTranslation} from "react-i18next"
import {useEffect, useRef, useState} from "react"
import {deviceAnalysisStats} from "../../../../../components/AnalysisStats"
import {APIv1} from "../../../../../api"
import "../BaseDeviceAnalysis.css"

const {DeviceMonitoringPieChart} = deviceAnalysisStats

const DeviceAnalysis = ({searchTerm}) => {
    const {t} = useTranslation()

    const [userData, setUserData] = useState(null)
    const [chartData, setChartData] = useState([])
    const [highlightedCard, setHighlightedCard] = useState(null)
    const chartRefs = useRef({})

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("user"))
        if (storedUser) {
            setUserData(storedUser)
        }
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.token) return

            try {
                const response = await APIv1.get("analysis/device/activity/", {
                    headers: {
                        Authorization: `Token ${userData.token}`,
                    },
                })

                const results = response?.data
                if (!Array.isArray(results)) {
                    console.warn("Unexpected API response:", response?.data)
                    return
                }

                const transformedData = results.map((item) => {
                    // Extract version data from the nested versions object
                    const versions = item.versions || {}
                    const versionKeys = Object.keys(versions).filter((key) => key.startsWith("v"))

                    return {
                        title: item.company_name ?? "Unknown",
                        companyInn: item.company_inn ?? "Unknown",
                        totalDeviceCount: item.device_count ?? 0,
                        totalFboxVersion: item.total_fbox_version ?? versionKeys.length,
                        versions: versions, // Pass the entire versions object
                        openShift: item.open_shift ?? 0,
                        closedShift: item.closed_shift ?? 0,
                        errors: item.errors ?? 0,
                        inactiveDevicesList: item.inactive_devices_serial_number ?? null,
                    }
                })

                setChartData(transformedData)
            } catch (err) {
                console.error("Failed to fetch device activity:", err)
            }
        }

        fetchData()
    }, [userData])

    // Auto-scroll to the searched card and shine
    useEffect(() => {
        if (searchTerm.trim() === "") return

        const match = chartData.find((item) => item.title.toLowerCase().includes(searchTerm.trim().toLowerCase()))

        if (match && chartRefs.current[match.title]) {
            // Scroll to card
            chartRefs.current[match.title].scrollIntoView({behavior: "smooth", block: "start"})

            // Highlight card
            setHighlightedCard(match.title)

            // Remove highlight after 1.2s (match animation duration)
            const timeout = setTimeout(() => {
                setHighlightedCard(null)
            }, 2800)

            return () => clearTimeout(timeout)
        }
    }, [searchTerm, chartData])

    return (
        <div className="charts-grid">
            {chartData.map((data, index) => (
                <div
                    key={index}
                    ref={(el) => {
                        chartRefs.current[data.title] = el
                    }}
                    className={highlightedCard === data.title ? "charts-grid--card highlight-card" : ""}
                >
                    <DeviceMonitoringPieChart
                        title={data.title}
                        companyInn={data.companyInn}
                        userToken={userData?.token}
                        totalDeviceCount={data.totalDeviceCount}
                        totalFboxVersion={data.totalFboxVersion}
                        versions={data.versions}
                        openShift={data.openShift}
                        closedShift={data.closedShift}
                        errors={data.errors}
                        inactiveDevicesList={data.inactiveDevicesList}
                        onDelete={(record) => {
                            setChartData((prev) => prev.filter((item) => item.companyInn !== record.company_inn))
                        }}
                    />
                </div>
            ))}
        </div>
    )
}

export default DeviceAnalysis
